import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { accountingApi } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingDashboardPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["accounting-dashboard", hospitalId],
    queryFn: () => accountingApi.getDashboard(hospitalId),
    enabled: !!hospitalId,
  });

  const ensureFy = useMutation({
    mutationFn: () => accountingApi.ensureFy(hospitalId),
    onSuccess: () => {
      toast.success("Financial year & periods ready");
      qc.invalidateQueries({ queryKey: ["accounting-dashboard", hospitalId] });
      qc.invalidateQueries({ queryKey: ["accounting-years", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Failed to ensure FY"),
  });

  const seedCoa = useMutation({
    mutationFn: () => accountingApi.seedCoa(hospitalId) as Promise<{ accountsCreated?: number }>,
    onSuccess: (r) => {
      toast.success(
        `Chart of accounts seeded${r?.accountsCreated != null ? ` (${r.accountsCreated} new)` : ""}`
      );
      qc.invalidateQueries({ queryKey: ["accounting-dashboard", hospitalId] });
      qc.invalidateQueries({ queryKey: ["accounting-accounts", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Failed to seed CoA"),
  });

  const dash = data as {
    accountCount?: number;
    draftCount?: number;
    postedCount?: number;
    openPeriods?: number;
    phase?: string;
    note?: string;
  } | undefined;

  return (
    <div className="space-y-4">
      {dash?.note ? (
        <p className="text-sm text-slate-600">{dash.note}</p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Ledger accounts", value: dash?.accountCount },
          { label: "Draft vouchers", value: dash?.draftCount },
          { label: "Posted vouchers", value: dash?.postedCount },
          { label: "Open periods", value: dash?.openPeriods },
        ].map((c) => (
          <Card key={c.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {c.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {isLoading ? "…" : c.value ?? 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {canPost ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={ensureFy.isPending}
            onClick={() => ensureFy.mutate()}
          >
            Ensure FY & periods
          </Button>
          <Button
            variant="outline"
            disabled={seedCoa.isPending}
            onClick={() => seedCoa.mutate()}
          >
            Seed system CoA
          </Button>
          <Button asChild>
            <Link to="vouchers/new">New journal voucher</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="opening-balances">Opening balances</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
